import PleasedChatClient from '.';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

export type Debounced<T extends (...a: any[]) => any> = ((...args: Parameters<T>) => void) & { cancel: () => void };

export function debounce<T extends (...a: any[]) => any>(func: T, delay = 250): Debounced<T> {
  let timer: null | number = null;

  const debounced = (...args: Parameters<T>) => {
    // @ts-ignore
    const context = <any>this;

    timer && clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(context, args);
    }, delay) as unknown as number; // TODO: Fix nodejs types in tsconfig.
  };

  debounced.cancel = () => {
    timer && clearTimeout(timer);
    timer = null;
  };

  return debounced;
}

export function throttle<T extends (...a: any[]) => any>(func: T, delay = 250): (...args: Parameters<T>) => void {
  let lastCall = 0;
  let timeoutId: number | null = null;

  return (...args) => {
    const now = Date.now();

    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    } else {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(
        () => {
          lastCall = Date.now();
          func(...args);
        },
        delay - (now - lastCall)
      ) as unknown as number;
    }
  };
}

/**
 * Trim a string from the right side with the given characters.
 * @param input The string to trim
 * @param characters The characters to trim
 */
export function rightTrim(input: string, characters: string) {
  let end = input.length - 1;
  while (characters.indexOf(input[end]) >= 0) {
    end -= 1;
  }
  return input.substr(0, end + 1);
}

export const getFileBuffer = (file: File) =>
  new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.addEventListener('load', function () {
      resolve(this.result);
    });
    fileReader.addEventListener('error', () => {
      reject('Failed to Parse Selected File');
    });
    fileReader.readAsArrayBuffer(file);
  });

// export function markSearchTextToHtml(haystack: string, needle: string) {
//   return haystack.replace(new RegExp(needle + '(?!([^<]+)?<)', 'gi'), '<mark class="highlighted-mark">$&</mark>')
// }
export function markSearchTextToHtml(haystack: string, needle: string) {
  // Escape special characters in the needle and split it into words
  const words = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').split(' ');

  // Create a regular expression pattern to match each word
  const pattern = words
    .map((word) => {
      if (word.length <= 3) {
        // Match the word at the start of a word boundary
        return `\\b${word}`;
      } else {
        // Match the word anywhere in the string
        return `${word}`;
      }
    })
    .join('|');

  // Use the pattern to replace occurrences of the words with HTML <span> tags
  return haystack.replace(new RegExp(pattern, 'gi'), '<mark class="highlighted-mark">$&</mark>');
}

/**
 * Detect ios version
 * @see https://stackoverflow.com/a/50035797
 */
export function getIOSDetails() {
  try {
    const agent = navigator?.userAgent || '';
    if (!agent || !agent.match(/ipad|iphone|ipod/i)) return null;
    //if the current device is an iDevice
    return {
      reported: agent.match(/OS (\d)?\d_\d(_\d)?/i)?.[0],
      major: agent.match(/OS (\d)?\d_\d(_\d)?/i)?.[0]?.split('_')?.[0],
      minor: agent.match(/OS (\d)?\d_\d(_\d)?/i)?.[0]?.replace(/_/g, '.'),
      majorNum: +(
        agent
          .match(/OS (\d)?\d_\d(_\d)?/i)?.[0]
          .split('_')[0]
          .replace('OS ', '') || ''
      ),
      minorNum: +(
        agent
          .match(/OS (\d)?\d_\d(_\d)?/i)?.[0]
          .replace('_', '.')
          .replace('_', '')
          .replace('OS ', '') || ''
      ),
    };
  } catch (e) {
    logger.error('[iOS Detection] Error detecting ios version', { error: parseErrorMessage(e) });
    return null;
  }
}

export function isVoiceChatSupported(platform: string) {
  return platform === 'ios' ? PleasedChatClient.isVoiceChatSupportedInApp : PleasedChatClient.isVoiceChatSupported;
}

function isPositiveInteger(x: string) {
  // http://stackoverflow.com/a/1019526/11236
  return /^\d+$/.test(x);
}

/**
 * Compare two software version numbers (e.g. 1.7.1)
 * Returns:
 *
 *  0 if they're identical
 *  negative if v1 < v2
 *  positive if v1 > v2
 *  Nan if they in the wrong format
 *
 *  E.g.:
 *
 *  assert(version_number_compare("1.7.1", "1.6.10") > 0);
 *  assert(version_number_compare("1.7.1", "1.7.10") < 0);
 *
 *  "Unit tests": http://jsfiddle.net/ripper234/Xv9WL/28/
 *
 *  Taken from http://stackoverflow.com/a/6832721/11236
 */

export function compareVersionNumbers(v1: string, v2: string) {
  var v1parts = v1.split('.');
  var v2parts = v2.split('.');

  // First, validate both numbers are true version numbers
  function validateParts(parts: string[]) {
    for (var i = 0; i < parts.length; ++i) {
      if (!isPositiveInteger(parts[i])) {
        return false;
      }
    }
    return true;
  }
  if (!validateParts(v1parts) || !validateParts(v2parts)) {
    return NaN;
  }

  for (var i = 0; i < v1parts.length; ++i) {
    if (v2parts.length === i) {
      return 1;
    }

    if (+v1parts[i] === +v2parts[i]) {
      continue;
    }
    if (+v1parts[i] > +v2parts[i]) {
      return 1;
    }
    return -1;
  }

  if (v1parts.length != v2parts.length) {
    return -1;
  }

  return 0;
}
